/**
 * Watchlist state
 */
import {
  ICompetitionWatchlistPair,
  ITicker,
  IWatchedTicker,
  enums,
} from '@kattana-io/common';
import {
  ADD_SUCCESS,
  REMOVE_SUCCESS,
  SET_TAB,
  SET_WATCHED,
  SET_GAINERS,
  SET_TVL,
  SET_COMPETITION,
  SET_TICKERS,
  SET_LOADING,
  UPDATE_SUCCESS,
} from '../actions/watchlist';

export const LS_WATCHLIST_KEY = 'kattana.watchlist';
export const LS_WATCHLIST_TAB_KEY = 'kattana.watchlist_tab';

export enum WatchlistTab {
  Star = 'Favourites',
  Gainers = 'Gainers',
  TVL = 'TVL',
  Competition = 'Competition',
}

export type UpdateWatchlistItemPayload =
  | {
      tag: enums.Tag;
    }
  | {
      old: number;
      new: number;
    }
  | {
      tag: enums.Tag;
      old: number;
      new: number;
    };

export interface IWatchlistState {
  tickers: Array<ITicker | IWatchedTicker>;
  tab: WatchlistTab;
  watched: IWatchedTicker[];
  gainers: ITicker[];
  tvl: ITicker[];
  competition: ICompetitionWatchlistPair[];
  loading: boolean;
}

const getDefaultState = (): IWatchlistState => {
  const savedTab = window.localStorage?.getItem(LS_WATCHLIST_TAB_KEY);
  return {
    tickers: [],
    tab: savedTab ? (savedTab as WatchlistTab) : WatchlistTab.TVL,
    watched: [],
    gainers: [],
    tvl: [],
    competition: [],
    loading: false,
  };
};

const defaultState = getDefaultState();

const addTickerSuccess = (
  state: IWatchlistState,
  ticker: IWatchedTicker
): IWatchlistState => {
  const watchlistItems = [ticker, ...state.watched].map((item, idx) => ({
    ...item,
    order: idx,
  }));
  if (state.tab === WatchlistTab.Star) {
    return {
      ...state,
      watched: watchlistItems,
      tickers: watchlistItems,
    };
  }
  return {
    ...state,
    watched: watchlistItems,
    tickers: state.tickers.map((item) =>
      item.id === ticker.id ? ticker : item
    ),
  };
};

const updateTickerSuccess = (
  state: IWatchlistState,
  { id, payload }: { id: number; payload: UpdateWatchlistItemPayload }
): IWatchlistState => {
  let watchlistItems = [...state.watched];
  if ('tag' in payload) {
    watchlistItems = watchlistItems.map((item) =>
      item.id === id ? { ...item, tag: payload.tag } : item
    );
    if (state.tab !== WatchlistTab.Star) {
      return {
        ...state,
        watched: watchlistItems,
        tickers: state.tickers.map((item) =>
          item.id === id ? { ...item, tag: payload.tag } : item
        ),
      };
    }
  }
  if ('new' in payload && 'old' in payload) {
    const [removed] = watchlistItems.splice(payload.old, 1);
    watchlistItems.splice(payload.new, 0, removed);
    watchlistItems = watchlistItems.map((item, idx) => ({
      ...item,
      order: idx,
    }));
  }
  if (state.tab === WatchlistTab.Star) {
    return {
      ...state,
      watched: watchlistItems,
      tickers: watchlistItems,
    };
  }
  return state;
};

const watchListReducer = (
  state: IWatchlistState = defaultState,
  action: any
): IWatchlistState => {
  switch (action.type) {
  case ADD_SUCCESS:
    return addTickerSuccess(state, action.payload);
  case REMOVE_SUCCESS:
    return {
      ...state,
      watched: state.watched.filter((item) => item.id !== action.payload),
      tickers:
        state.tab === WatchlistTab.Star
          ? state.tickers.filter((item) => item.id !== action.payload)
          : state.tickers,
    };
  case UPDATE_SUCCESS:
    return updateTickerSuccess(state, action.payload);
  case SET_TAB:
    return {
      ...state,
      tab: action.payload,
    };
  case SET_WATCHED:
    return {
      ...state,
      watched: action.payload,
    };
  case SET_GAINERS:
    return {
      ...state,
      gainers: action.payload,
    };
  case SET_TVL:
    return {
      ...state,
      tvl: action.payload,
    };
  case SET_COMPETITION:
    return {
      ...state,
      competition: action.payload,
    };
  case SET_TICKERS:
    return {
      ...state,
      tickers:
        state.tab === WatchlistTab.Star
          ? action.payload
          : action.payload.map((item: ITicker) => {
            const watchedItem = state.watched.find(
              (watchedTicker) => item.id === watchedTicker.id
            );
            return watchedItem || item;
          }),
    };
  case SET_LOADING:
    return {
      ...state,
      loading: action.payload,
    };
  default:
    return state;
  }
};

export default watchListReducer;
